import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Receipt } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { formatMinor } from "../../lib/finance";
import { canSeeBilling, canWrite, priceText, totalsByCurrency } from "../../lib/billing";
import { SubscriptionStatusBadge } from "./BillingBits";
import { Button } from "../ui/button";
import { Card } from "../ui/card";

// The customer account's « Abonnement » card (Ordre 17, D-62 A): only for a reader holding billing:view — managing the account or
// selling to the pharmacy shows no amount (D-61). The open subscription, and what is overdue on its installments.
export default function CustomerSubscriptionCard({ prospectId }) {
  const { user } = useAuth();
  const visible = canSeeBilling(user);
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!visible) return undefined;
    let alive = true;
    Promise.all([
      api.get("/billing/subscriptions", { params: { prospect_id: prospectId, state: "open", limit: 1 } }),
      api.get("/billing/installments", { params: { prospect_id: prospectId, state: "overdue", limit: 100 } }),
    ]).then(([subs, late]) => { if (alive) setState({ sub: subs.data.items[0] || null, late: late.data.items }); })
      .catch(() => { if (alive) setState({ error: true }); });
    return () => { alive = false; };
  }, [visible, prospectId]);
  if (!visible || !state || state.error) return null;
  const { sub, late } = state;
  const owed = totalsByCurrency(late);
  return (
    <Card className="p-5 space-y-3" data-testid="customer-subscription-card">
      <h3 className="flex items-center gap-2 font-semibold text-slate-900"><Receipt size={16} className="text-slate-400" />Abonnement</h3>
      {sub ? (
        <Link to={`/revenus/abonnements/${sub.id}`} className="flex items-start justify-between gap-2 rounded-lg -mx-2 px-2 py-1.5 hover:bg-slate-50">
          <div className="min-w-0 space-y-1">
            <div className="text-sm font-medium text-slate-900">{sub.offer.offer_name} · {priceText(sub.price_minor, sub.currency, sub.periodicity)}</div>
            <SubscriptionStatusBadge status={sub.status} />
          </div>
          <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
        </Link>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-slate-500">Aucun abonnement en cours.</p>
          {canWrite(user, "billing:manage_subscriptions") && (
            <Button asChild size="sm" variant="outline"><Link to={`/revenus/abonnements/nouveau?prospect=${prospectId}`}>Créer l'abonnement</Link></Button>
          )}
        </div>
      )}
      {late.length > 0 && (
        <Link to="/revenus/factures?tab=overdue" className="block text-sm text-red-700 hover:underline" data-testid="customer-subscription-overdue">
          {late.length} échéance{late.length > 1 ? "s" : ""} en retard · {Object.entries(owed).map(([c, v]) => formatMinor(v, c)).join(" · ")}
        </Link>
      )}
    </Card>
  );
}
